import Button from '@atoms/Button/Button';
import Drawer from '@atoms/Drawer/Drawer';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IFilterGroup } from '@models/IFilterGroup';
import { requestState } from '@models/IListing';
import { QueryState } from '@models/IListingWrapper';
import FilterAccordion from '@molecules/FilterAccordion/FilterAccordion';
import {
  useListingDispatch,
  useListingState,
} from '@utility/context/ListingContext';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import { ActionType } from '@utility/reducers/ListingWrapper';
import { MouseEvent } from 'react';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

interface Props {
  readonly applyLabel: string;
  readonly clearAllLabel: string;
  readonly closeLabel: string;
  readonly data: requestState;
  readonly filterLabel: string;
  readonly filters: IFilterGroup[];
  readonly listingFilterParam?: string;
  readonly mappedCheckedDrawerFilters: string[];
  readonly permanentFilters?: string[];
  readonly queryState: QueryState;
  readonly register: UseFormRegister<QueryState>;
  readonly setValue: UseFormSetValue<QueryState>;
  readonly site: SiteEnum;
  readonly type: ListingTypeEnum;
  readonly watch: UseFormWatch<QueryState>;
  readonly handleFormSubmit: (e: React.MouseEvent<HTMLElement>) => void;
  readonly onClearAll: (
    e: MouseEvent<HTMLButtonElement>,
    params?: Partial<QueryState>
  ) => void;
}

const ListingFilterDrawer = ({
  applyLabel,
  clearAllLabel,
  closeLabel,
  data,
  filterLabel,
  filters,
  handleFormSubmit,
  listingFilterParam,
  mappedCheckedDrawerFilters,
  onClearAll,
  permanentFilters,
  queryState,
  register,
  setValue,
  site,
  type,
  watch,
}: Props) => {
  const { isMobileFiltersOpen, promoCardReferences } = useListingState();
  const dispatch = useListingDispatch();
  const isMobile = useMediaQuery(1024);

  return (
    <Drawer
      closeLabel={closeLabel}
      containerClasses="!tw-px-xs !tw-py-sm"
      isOpen={isMobileFiltersOpen}
      site={site}
      slideFromLeft
      title={filterLabel}
      wrapperStyles="tw-h-full tw-flex tw-flex-col"
      setIsOpen={(isOpen: boolean) =>
        dispatch({ type: ActionType.MOBILE_FILTERS_OPEN, payload: isOpen })
      }
    >
      <form className="tw-flex tw-h-full tw-flex-col">
        <div className="tw-overflow-visible tw-pb-[90px]">
          <FilterAccordion
            firstOpen
            allowMultipleOpen={isMobile}
            variant="listingFilter"
            setValue={setValue as any}
            register={register as any}
            watch={watch as any}
            filters={filters}
            className="max-md:tw-pb-14"
            facets={data.facets}
            promoCardRefs={promoCardReferences}
            permanentFilters={permanentFilters}
            onPromoCardReference={(newRefs: number[]) => {
              dispatch({
                type: ActionType.PROMO_CARD_REFERENCES,
                payload: newRefs,
              });
              setValue('promoCardReference', newRefs[0] || 0);
            }}
            showFilterCount={
              site === SiteEnum.B2B && type === ListingTypeEnum.Product
            }
          />
        </div>
        <div className="tw-fixed tw-bottom-0 tw-left-0 tw-mt-auto tw-flex tw-w-full tw-bg-white md:tw-sticky">
          <Button
            variant="grey80"
            modifier="outline"
            buttonClasses="tw-basis-6/12 tw-rounded-none"
            label={clearAllLabel}
            onClick={(e) => {
              onClearAll(e, {
                freetext: queryState.freetext, // maintain search term
                [listingFilterParam ?? '']:
                  queryState?.[listingFilterParam ?? ''], // maintain listing filters
              });
            }}
            disabled={!mappedCheckedDrawerFilters.length}
          />
          <Button
            variant={site === SiteEnum.B2B ? 'yellow' : 'grey80'}
            buttonClasses="tw-basis-6/12 tw-rounded-none"
            onClick={handleFormSubmit}
            label={applyLabel}
          />
        </div>
      </form>
    </Drawer>
  );
};

export default ListingFilterDrawer;
